/**
 * Job site address service — "this visit is somewhere else".
 *
 * The problem this solves: a job inherits its address from the deal / customer
 * record when it's created. Reality moves faster than the CRM — the customer
 * bought a new place and we haven't migrated them yet, the gear is going to a
 * holiday house, the tech has to meet someone at a different site. Until now
 * the office typed the real address into the scheduling notes and hoped the
 * tech read them, which left the nav link, the ETA text, the office map and the
 * post-job report all pointing at the wrong house.
 *
 * The fix: change the address ON THE JOB. `Job.address` / `loc` / `lat` / `lng`
 * stay the single source of truth for where the tech is going, so every
 * downstream consumer keeps working untouched — nav links, `sendEtaSms`, the
 * office map, briefings, PDFs. `Job.addressOverride` records that the value was
 * hand-set (by whom, why, and what it was before) so the UI can badge the job
 * and offer a one-tap revert.
 *
 * The customer record is NEVER written by this service. Saving the new address
 * to the customer's address book is a separate, explicitly opt-in action — see
 * `saveToCustomerBook` — because the whole point of an ad-hoc change is that
 * the customer hasn't moved on paper yet.
 */

import { doc, updateDoc, arrayUnion, deleteField, FieldValue } from 'firebase/firestore';
import { db } from '@/firebase';
import type { Job, JobSiteAddressOverride } from '@/types/schema';
import { stripUndefinedDeep } from '@/utils/firestore';
import { buildJobEvent, currentActor } from '@/services/job-event.service';
import { geocodeAddress } from '@/services/geocode.service';
import { addCustomerAddress } from '@/services/customer.service';
// The matching normalizer (NOT geocode.service's cache-key one — see the
// docstring there). Same rule the address book dedups with, so "is this
// actually a different address?" answers consistently in both places.
// `composeSiteAddress` moved next to it so every record type — job, customer,
// deal — composes an address the same way; re-exported below so the existing
// importers of this module are unaffected.
import { normalizeAddress, composeSiteAddress } from '@/utils/address';
import type { SiteAddressParts } from '@/utils/address';

/** True when this job's site address was hand-set away from the record address. */
export function hasSiteAddressOverride(job: Pick<Job, 'addressOverride'> | null | undefined): boolean {
  return !!job?.addressOverride?.previous;
}

/**
 * One-line "what it was before" for the revert affordance / tooltip.
 * Empty string when the job has no override (callers can render nothing).
 */
export function originalSiteAddress(job: Pick<Job, 'addressOverride'> | null | undefined): string {
  const prev = job?.addressOverride?.previous;
  if (!prev) return '';
  return prev.address || prev.loc || '';
}

// ─── Reading the site address ───────────────────────────────────────────────
//
// `job.address` is the street line and nothing else. Deal / fault jobs created
// before the office captured a street (or from a customer record that only
// carried a suburb) land with `address: ''` and `loc: 'BOWRAL'` — so every
// surface doing `job.address || job.loc` renders a suburb and calls it the
// address. On the read-only team-schedule sheet that's all a covering tech
// gets, and the Navigate button then routes them to the middle of the suburb.
//
// These helpers compose the fullest address the records can support: the job's
// own parts first, topped up from the customer record ONLY when the job has no
// street of its own. All pure — unit-tested in job-address.test.ts.

export type { SiteAddressParts };
export { composeSiteAddress };

function tidy(value: string | null | undefined): string {
  return (value || '').trim();
}

/**
 * True when `haystack` already contains `needle` as a run of whole tokens.
 *
 * Token-exact, NOT substring: a plain `includes` says "13 Sandy Bay Rd"
 * contains the state "SA", and we'd then match the wrong address-book row.
 */
function containsPhrase(haystack: string, needle: string): boolean {
  const need = normalizeAddress(needle).split(' ').filter(Boolean);
  if (need.length === 0) return true;
  const hay = normalizeAddress(haystack).split(' ').filter(Boolean);
  for (let i = 0; i + need.length <= hay.length; i++) {
    if (need.every((tok, j) => hay[i + j] === tok)) return true;
  }
  return false;
}

/** Address-book row / legacy single-address block on a customer record. */
type CustomerAddressLike = SiteAddressParts & { isPrimary?: boolean };

/** Just enough of a Customer to read an address off. */
export interface CustomerAddressSource {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  addresses?: CustomerAddressLike[];
}

/**
 * Best address on a customer record for a job in `suburbHint`.
 *
 * The hint is a GUARD, not a preference: customers routinely hold several
 * sites, and handing a tech the wrong one is worse than handing them a suburb.
 * With a hint we only return a row that agrees with it (by city or by the
 * street line naming the suburb); with no hint we take the primary.
 */
export function customerSiteAddress(
  customer: CustomerAddressSource | null | undefined,
  suburbHint = '',
): string {
  if (!customer) return '';

  const rows: CustomerAddressLike[] = [
    ...(customer.addresses ?? []),
    {
      address: customer.address,
      city: customer.city,
      state: customer.state,
      postcode: customer.postcode,
    },
  ].filter((row) => tidy(row.address));

  const hint = tidy(suburbHint);
  const usable = hint
    ? rows.filter(
        (row) => containsPhrase(tidy(row.city), hint) || containsPhrase(tidy(row.address), hint),
      )
    : rows;

  const chosen = usable.find((row) => row.isPrimary) || usable[0];
  return chosen ? composeSiteAddress(chosen) : '';
}

/**
 * The fullest site address we can show for a job — what every read surface
 * should render and navigate to, in place of `job.address || job.loc`.
 *
 * A hand-set one-off address is authoritative and never topped up from the
 * customer record: the whole point of an override is that the customer record
 * describes somewhere else (see the module docstring).
 */
export function resolveSiteAddress(
  job: (SiteAddressParts & Pick<Job, 'addressOverride'>) | null | undefined,
  customer?: CustomerAddressSource | null,
): string {
  if (!job) return '';
  const fromJob = composeSiteAddress(job);
  if (tidy(job.address) || hasSiteAddressOverride(job)) return fromJob;

  // No street on the job at all — the customer record is the only place the
  // street number lives. Guarded on the job's suburb so a multi-site customer
  // can't redirect the tech.
  return customerSiteAddress(customer, tidy(job.city) || tidy(job.loc)) || fromJob;
}

export interface ChangeJobSiteAddressOptions {
  jobId: string;
  /** The job as currently loaded — used to snapshot the pre-override address. */
  job: Pick<Job, 'address' | 'loc' | 'lat' | 'lng' | 'addressOverride' | 'customerId'>;
  /** New street address for this visit. Required. */
  address: string;
  /** New suburb / location description. Falls back to the existing one. */
  loc?: string;
  /**
   * Coordinates for the new address. Omit to geocode `address` here — the map,
   * drive-time and ETA surfaces all key off lat/lng, so a changed address with
   * stale coordinates is worse than no change at all.
   */
  lat?: number;
  lng?: number;
  /** Why this visit is somewhere else — surfaces on the tech's job card. */
  reason?: string;
  /** Acting user. Defaults to the current session. */
  userId?: string;
  userName?: string;
  /**
   * Opt-in ONLY. When true, also files the new address in the customer's saved
   * address book so it's reusable next time. Default false — an ad-hoc change
   * must not mutate the customer record.
   */
  saveToCustomerBook?: boolean;
  /** Label for the address-book entry when `saveToCustomerBook` is set. */
  addressBookLabel?: string;
  /** City / state / postcode for the address-book entry. Job fields don't carry these. */
  addressParts?: { city?: string; state?: string; postcode?: string };
}

/**
 * Point a job at a different site for this visit.
 *
 * Writes the address, its coordinates, the provenance block and an audit event
 * in a single `updateDoc` so the job can never be seen half-changed. Returns
 * the resolved coordinates so the caller can confirm the pin moved.
 *
 * Throws when the address is blank or unchanged — the modal should close and
 * discard rather than write a no-op audit row.
 */
export async function changeJobSiteAddress(
  opts: ChangeJobSiteAddressOptions,
): Promise<{ lat: number; lng: number; geocoded: boolean }> {
  const {
    jobId,
    job,
    address,
    loc,
    lat,
    lng,
    reason,
    userId,
    userName,
    saveToCustomerBook,
    addressBookLabel,
    addressParts,
  } = opts;

  if (!jobId) throw new Error('jobId is required');

  const nextAddress = (address || '').trim();
  if (!nextAddress) throw new Error('Enter a site address for this job');

  const nextLoc = (loc ?? job.loc ?? '').trim();
  const currentAddress = (job.address || '').trim();
  const coordsSupplied = typeof lat === 'number' && typeof lng === 'number';

  // Compare normalized so punctuation/case/whitespace noise between a typed
  // address and the same address in the customer's book doesn't read as a move.
  const addressChanged = normalizeAddress(nextAddress) !== normalizeAddress(currentAddress);

  if (!addressChanged && nextLoc === (job.loc || '').trim() && !coordsSupplied) {
    throw new Error('That is already this job’s site address');
  }

  // Resolve coordinates. A failed geocode is not fatal — the address text is
  // what gets the tech there; the pin just stays where it was (and the map
  // backfill retries on next view).
  let resolvedLat = job.lat;
  let resolvedLng = job.lng;
  let geocoded = false;
  if (coordsSupplied) {
    resolvedLat = lat as number;
    resolvedLng = lng as number;
    geocoded = true;
  } else {
    const hit = await geocodeAddress(nextAddress).catch(() => null);
    if (hit) {
      resolvedLat = hit.lat;
      resolvedLng = hit.lng;
      geocoded = true;
    }
  }

  const who = currentActor();
  const actor = userId || who.actor;
  const actorName = userName || who.actorName;
  const now = new Date().toISOString();

  // Snapshot the ORIGINAL address once. Re-pointing an already-overridden job
  // keeps the first snapshot so "revert" always means "back to the address the
  // job was created with", not "back to the previous ad-hoc guess".
  const previous: JobSiteAddressOverride['previous'] = job.addressOverride?.previous ?? {
    address: currentAddress,
    loc: job.loc || undefined,
    lat: typeof job.lat === 'number' ? job.lat : undefined,
    lng: typeof job.lng === 'number' ? job.lng : undefined,
  };

  const override: JobSiteAddressOverride = {
    reason: reason?.trim() || undefined,
    previous,
    setBy: actor,
    setByName: actorName,
    setAt: now,
  };

  const why = reason?.trim() ? ` (${reason.trim()})` : '';
  const summary = !addressChanged
    ? `Site suburb set to ${nextLoc || '—'}`
    : currentAddress
      ? `Site address changed: ${currentAddress} → ${nextAddress}${why}`
      : `Site address set to ${nextAddress}${why}`;

  // ONLY a genuine address change earns the one-off-site badge. Picking a book
  // entry that happens to carry no city looks like a change to the form (the
  // suburb differs) but leaves the job on the same street address — stamping an
  // override there would badge the job as a one-off site whose "usual address"
  // is the address it is already on. An already-overridden job keeps its
  // override through a suburb-only correction.
  const stampOverride = addressChanged || !!job.addressOverride;

  await updateDoc(
    doc(db, 'jobs', jobId),
    stripUndefinedDeep({
      address: nextAddress,
      loc: nextLoc,
      lat: resolvedLat,
      lng: resolvedLng,
      ...(stampOverride ? { addressOverride: override } : {}),
      eventLog: arrayUnion(
        buildJobEvent('address_changed', summary, { actor, actorName }),
      ) as unknown as FieldValue,
      updatedAt: now,
    }),
  );

  // Opt-in only, and best-effort: the job write is the deliverable, a failed
  // address-book update must not surface as a failed address change.
  if (saveToCustomerBook && job.customerId) {
    try {
      await addCustomerAddress(job.customerId, {
        label: addressBookLabel?.trim() || 'Site',
        address: nextAddress,
        city: addressParts?.city?.trim() || nextLoc || undefined,
        state: addressParts?.state?.trim() || undefined,
        postcode: addressParts?.postcode?.trim() || undefined,
        country: 'Australia',
        isPrimary: false,
        source: 'manual',
      });
    } catch (err) {
      console.error('[job-address] Failed to save address to customer book:', err);
    }
  }

  return { lat: resolvedLat, lng: resolvedLng, geocoded };
}

export interface RevertJobSiteAddressOptions {
  jobId: string;
  job: Pick<Job, 'address' | 'addressOverride'>;
  userId?: string;
  userName?: string;
}

/**
 * Put the job back on the address it carried before the first override, and
 * drop the provenance block so the badge clears.
 */
export async function revertJobSiteAddress(opts: RevertJobSiteAddressOptions): Promise<void> {
  const { jobId, job, userId, userName } = opts;
  if (!jobId) throw new Error('jobId is required');

  const previous = job.addressOverride?.previous;
  if (!previous) throw new Error('This job is already on its original address');

  const who = currentActor();
  const actor = userId || who.actor;
  const actorName = userName || who.actorName;
  const now = new Date().toISOString();

  const summary = `Site address reverted: ${(job.address || '').trim() || '—'} → ${previous.address || '—'}`;

  // Older overrides may have snapshotted an address without coordinates.
  // Restoring the text but leaving the overridden pin behind would put the map
  // and the nav link at odds, so re-resolve when the snapshot is coordinate-less.
  let lat = previous.lat;
  let lng = previous.lng;
  if ((typeof lat !== 'number' || typeof lng !== 'number') && previous.address) {
    const hit = await geocodeAddress(previous.address).catch(() => null);
    if (hit) {
      lat = hit.lat;
      lng = hit.lng;
    }
  }

  await updateDoc(doc(db, 'jobs', jobId), {
    ...stripUndefinedDeep({
      address: previous.address || '',
      loc: previous.loc || '',
      lat,
      lng,
      updatedAt: now,
    }),
    addressOverride: deleteField(),
    eventLog: arrayUnion(buildJobEvent('address_changed', summary, { actor, actorName })),
  });
}
